import type { AssemblyId, ContactId, CustomerId, GrantId, PropertyId, AttachmentId } from "../shared/ids.ts";

/** Who pays / who the account is. Distinct from property owner and from contacts. */
export interface Customer {
  readonly id: CustomerId;
  displayName: string;
  billingAddress: PostalAddress | null;
  /**
   * Billing preferences (buildbook "Customers … billing preferences"; D183): the primary billing
   * contact is preselected when emailing invoices and receipts, only while they still hold billing
   * permission and a verified email; PAPER means the owner prints and mails. Nothing sends by itself.
   */
  billingPreferences?: { primaryContactId: ContactId | null; delivery: "EMAIL" | "PAPER"; readonly updatedAtUtc: string } | null;
  revision: number;
  readonly createdAtUtc: string;
}

/** A person. Having a contact record grants nothing by itself; grants do. */
export interface Contact {
  readonly id: ContactId;
  fullName: string;
  email: string | null;
  emailVerified: boolean;
  phone: string | null;
  /**
   * "How do you want us to contact you?" (owner, 2026-10-17): email, call/text, or both. Absent on
   * contacts made before the question existed (treat as not asked). Bills, receipts and reports still
   * go by email; this tells the office and the tester how to reach the person.
   */
  contactBy?: ContactBy;
  revision: number;
}

export interface ContactBy {
  readonly email: boolean;
  readonly callText: boolean;
}

/**
 * Explicit, independent permissions. Booking, onsite access, report receipt and
 * billing are separate: holding one never implies another (buildbook B1, D80, D91, D105).
 */
export type GrantRole = "BILLING" | "REPORTS" | "SCHEDULING" | "ONSITE_ACCESS" | "PRIMARY_CONTACT";

export type GrantScope =
  | { readonly kind: "CUSTOMER"; readonly customerId: CustomerId }
  | { readonly kind: "PROPERTY"; readonly customerId: CustomerId; readonly propertyId: PropertyId };

export interface ContactGrant {
  readonly id: GrantId;
  readonly contactId: ContactId;
  readonly scope: GrantScope;
  readonly role: GrantRole;
  readonly grantedAtUtc: string;
  revokedAtUtc: string | null;
}

export interface PostalAddress {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode: string;
}

export interface Property {
  readonly id: PropertyId;
  name: string;
  address: PostalAddress;
  /** Address verification gates navigation (D85); never guess a destination. */
  addressVerified: boolean;
  /** Water purveyor assignment must come from verified evidence, not ZIP guesses. */
  waterAuthority: AuthorityAssignment;
  /**
   * Where test reports are filed when that isn't the water purveyor (buildbook: "distinguish water
   * purveyor from reporting authority"; e.g. a consecutive system whose wholesaler runs the backflow
   * program). Verified from a stated source like the purveyor; absent = reports go to the purveyor.
   */
  reportingAuthority?: { readonly authorityName: string; readonly sourceReference: string; readonly verifiedAtUtc: string } | null;
  accessNotes: string;
  /** Commercial properties pay the per-inch size charge (finance/invoices.ts). Absent = residential. */
  kind?: "RESIDENTIAL" | "COMMERCIAL";
  /** Where this customer came from (commerce/leads.ts); absent = not recorded. */
  source?: { readonly leadSourceId: string; readonly detail: string | null; readonly setAtUtc: string };
  /**
   * Who pays for this property's tests when nobody paid up front (PropertyParty bill-to): a linked
   * customer chosen on purpose, e.g. the landlord who owns it rather than the tenant who booked.
   * Absent = the customer on each job. Prepaid (booked) and contract jobs always stay with their payer.
   */
  billTo?: { readonly customerId: CustomerId; readonly reason: string; readonly setAtUtc: string } | null;
  revision: number;
}

export type AuthorityAssignment =
  | { readonly status: "UNVERIFIED" }
  | { readonly status: "VERIFIED"; readonly authorityName: string; readonly sourceReference: string; readonly verifiedAtUtc: string };

/** How a customer stands to a property; distinguishes the owner from the occupant or manager (buildbook: Customers). */
export type PropertyRelationship = "OWNER" | "OCCUPANT" | "MANAGER" | "OTHER";

export interface CustomerPropertyLink {
  readonly customerId: CustomerId;
  readonly propertyId: PropertyId;
  /** Absent = not recorded yet. Recording it grants nothing; roles stay separate grants. */
  readonly relationship?: PropertyRelationship;
}

export type AssemblyType = "RPZ" | "DCVA" | "PVB";
export const ASSEMBLY_TYPES: readonly AssemblyType[] = ["RPZ", "DCVA", "PVB"];

import type { LocationChange } from "../work/access.ts";

export type LocationClass = "INSIDE" | "OUTSIDE" | "UNKNOWN";

export interface AuthorityAssemblyIdentifier {
  readonly authorityName: string;
  readonly value: string;
  readonly effectiveFromUtc: string;
  supersededAtUtc: string | null;
}

export interface Assembly {
  /** Permanent internal identifier; never reused, never equal to an authority ID. */
  readonly id: AssemblyId;
  readonly propertyId: PropertyId;
  type: AssemblyType;
  manufacturer: string | null;
  model: string | null;
  serialNumber: string | null;
  sizeInches: string | null;
  locationClass: LocationClass;
  /** Customer's own words, stored verbatim and never parsed into locationClass (D56). */
  locationDescription: string;
  /** Customer's photos of where to find it (work/access.ts); absent on older records. */
  locationPhotoIds?: AttachmentId[];
  /** Every location answer, oldest first (D58); absent until the first correction. */
  locationHistory?: LocationChange[];
  authorityIds: AuthorityAssemblyIdentifier[];
  active: boolean;
  /** Retired because the customer replaced it: when, and the device that took its place. History stays. */
  replacedBy?: { readonly assemblyId: AssemblyId; readonly atUtc: string } | null;
  /** This device replaced an earlier one at the same spot. */
  replaces?: AssemblyId | null;
  /**
   * Irrigation cap-off (work/custom.ts): CAPPED once the water supplier accepted it (the device is then
   * inactive: no reminders or next-year job). Back to null when reconnected. History is kept in capHistory.
   */
  capped?: { readonly jobId: string; readonly atUtc: string; readonly supplierEvidence: string } | null;
  /**
   * Type not confirmed yet (batch 23, 1A): booked online without a type. NEEDS_MODEL until the tester enters the
   * make and model; WAITING_FOR_OWNER when that model isn't on the list. Null/absent = the type is confirmed.
   */
  typeCheck?: { readonly status: "NEEDS_MODEL" | "WAITING_FOR_OWNER"; readonly foundAtUtc: string | null } | null;
  capHistory?: { readonly kind: "CAPPED" | "RECONNECTED"; readonly atUtc: string; readonly jobId: string | null; readonly by: string; readonly note: string | null }[];
  revision: number;
}

/** Pipe size in inches from what's written on the assembly ("3/4", "1 1/2", "1-1/2", "2\"", "1.5"); null if unreadable. */
export function parsePipeInches(text: string | null | undefined): number | null {
  const t = (text ?? "").replace(/["”″]|in(ch(es)?)?\.?$/gi, "").trim();
  if (!t) return null;
  let m = /^(\d+)(?:[ -](\d+)\/(\d+))?$/.exec(t);
  if (m) return Number(m[1]) + (m[2] ? Number(m[2]) / Number(m[3]) : 0);
  m = /^(\d+)\/(\d+)$/.exec(t);
  if (m && Number(m[2]) > 0) return Number(m[1]) / Number(m[2]);
  m = /^\d+(\.\d+)?$/.exec(t);
  if (m) return Number(t);
  return null;
}
